import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Server } from '../../server/server';
import AlertModal from '../modals/AlertModal';
import MessageModal from '../modals/MessageModal';
import QuestionModal from '../modals/QuestionModal';
import AddMediaModal from '../modals/AddMediaModal';
import AddEmbedModal from '../modals/AddEmbedModal';
import { getFirstParagraph, getPublicVideoUrl, isVideoUrl } from '../util/util';

let IMAGE_MIME_REGEX = /^image\/(p?jpeg|gif|png)$/i;

const Quill = ReactQuill.Quill;
const Delta = Quill.import('delta');
const BlockEmbed = Quill.import('blots/block/embed');

export class AudioBlot extends BlockEmbed {
  static blotName = 'audio';
  static tagName = 'audio';

  static create(url: any) {
    console.log('AudioBlot::create');
    let node = super.create();
    node.setAttribute('class', 'ql-audio');
    node.setAttribute('src', url);
    node.setAttribute('controls', '');
    return node;
  }
  
  static value(node: any) {
    return node.getAttribute('src');
  }
}

export class EmbedBlot extends BlockEmbed {
  static blotName = 'embed';
  static tagName = 'div';

  static create(data:any) {
    console.log('EmbedBlot::create');
    let node = super.create();

    if(data.video) node.dataset.video = data.video;
    if(data.spotify) node.dataset.spotify = data.spotify;

    let html = data.html;

    if(data.video) {
      node.classList.add('ql-embed-video');
    }

    if(data.spotify) {
      node.classList.add('ql-embed-spotify');
      html = html.replace('width="100%" ', '');
      html = html.replace('height="352" ', '');
    }

    node.classList.add('ql-embed');

    node.innerHTML = html;

    return node;
  }

  static value(node: HTMLElement) {
    let data:any = {html: node.innerHTML};
    if(node.dataset.video) data.video = node.dataset.video;
    if(node.dataset.spotify) data.spotify = node.dataset.spotify;
    return data;
  }
}

export class VideoBlot extends BlockEmbed {
  static blotName = 'video';
  static tagName = 'video';

  static create(url: string) {
    console.log('VideoBlot::create');
    let node = super.create();
    node.setAttribute('class', 'ql-video');
    node.setAttribute('controls', '');
    node.setAttribute('src', url);
    return node;
  }

  static value(node: any) {
    return node.getAttribute('src');
  }
}

export class ImageBlot extends BlockEmbed {
  static blotName = 'image';
  static tagName = 'div';

  static create(url: string) {
    console.log('ImageBlot::create');
    let node = super.create();
    node.dataset.url = url;
    let imageNode = document.createElement('img');
    imageNode.setAttribute('class', 'ql-editor-image');
    imageNode.setAttribute('src', url);
    node.appendChild(imageNode);
    return node;
  }

  static value(node: any) {
    return node.dataset.url;
  }
}

export class UnfurlBlot extends BlockEmbed {
  static blotName = 'unfurl';
  static tagName = 'div';

  static create(data: any) {
    console.log('UnfurlBlot::create');
    let node = super.create();

    if(!data.url || !data.title)
      return node;

    node.dataset.url = data.url;
    if(data.title) node.dataset.title = data.title;
    if(data.author) node.dataset.author = data.author;
    if(data.image) node.dataset.image = data.image;
    if(data.description) node.dataset.description = data.description;

    node.setAttribute('class', 'ql-unfurl');

    let author = data.author;

    if(!author && data.url) {
      let regex = /^(?:https?:\/\/)?(?:[^@\/\n]+@)?(?:www\.)?([^:\/?\n]+)/;
      let match = data.url.match(regex);
      author = match && match[1];
    }

    let html = '';
    html += `<a style="text-decoration:none" class="ql-unfurl-anchor" href="${data.url}" target="_blank">`;
    html +=   `<div class="ql-unfurl-container">`

    if(data.image)
    html +=     `<img class="ql-unfurl-image" src="${data.image}" />`;

    html +=     `<div class="ql-unfurl-text" contenteditable="false" suppressContentEditableWarning="true">`;
    html +=       `<div class="site-page-subtext">${author}</div>`;
    html +=       `<div class="ql-unfurl-title-text"><b>${data.title}</b></div>`;

    if(data.description)
    html +=       `<div>${data.description}</div>`;

    html +=     `</div>`;
    html +=   `</div>`;
    html += `</a>`;

    node.innerHTML = html;

    return node;
  }

  static value(node: HTMLElement) {
    let data:any = {url: node.dataset.url};
    if(node.dataset.title) data.title = node.dataset.title;
    if(node.dataset.author) data.author = node.dataset.author;
    if(node.dataset.image) data.image = node.dataset.image;
    if(node.dataset.description) data.description = node.dataset.description;
    return data;
  }
}

class LoadingBlot extends BlockEmbed {
  static blotName = 'loading';
  static tagName = 'div';

  static create(data: any) {
    let node = super.create();
    node.setAttribute('class', 'ql-loading');
    
    let html = '';
    html += `<div class="ql-loading-container">`;
    html +=   `<div class="ql-loading-spinner"></div>`;
    html +=   `<div class="ql-loading-text">Unfurling link...</div>`;
    html += `</div>`;
    
    node.innerHTML = html;
    return node;
  }
}

Quill.register(LoadingBlot, true);
Quill.register(UnfurlBlot, true);
Quill.register(AudioBlot, true);
Quill.register(EmbedBlot, true);
Quill.register(VideoBlot, true);
Quill.register(ImageBlot, true);

interface SharedQuillEditorProps {
  placeholder?: string;
  isActivity?: boolean;
  hasFontSize?: boolean;
  onChange?: Function;
  onKeyDown?: Function;
  getPosition?: Function;
  getRef: Function;
  content?: string;
  height?: string;
}

interface SharedQuillEditorState {
  content: string;
  openAddMedia: boolean;
  openAddEmbed: boolean;
  message: string;
  alert: string;
  deleteImageQuestion: string;
  pendingDeleteIndex: number | null;
  pendingDeleteSelection: { index: number; length: number } | null;
  isUnfurling: boolean;
}

class SharedQuillEditor extends React.Component<SharedQuillEditorProps, SharedQuillEditorState> {
  quillRef: any;
  quillPosition: number;
  reactQuillRef: any;
  quillModules: any;
  quillFormats: any;
  isPlainPaste: boolean;

  constructor(props: SharedQuillEditorProps) {
    super(props);

    this.state = {
      content: '',
      openAddMedia: false,
      openAddEmbed: false,
      message: '',
      alert: '',
      deleteImageQuestion: '',
      pendingDeleteIndex: null,
      pendingDeleteSelection: null,
      isUnfurling: false,
    }

    this.quillRef = null;
    this.reactQuillRef = null;
    this.quillPosition = 0;
    this.isPlainPaste = false;

    this.onContentChange = this.onContentChange.bind(this);
    this.onContentChangeSelection = this.onContentChangeSelection.bind(this);
    this.onContentKeyDown = this.onContentKeyDown.bind(this);
    this.onQuillImage = this.onQuillImage.bind(this);
    this.onEmbedOpen = this.onEmbedOpen.bind(this);
    this.onPaste = this.onPaste.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onConfirmDeleteImage = this.onConfirmDeleteImage.bind(this);
    this.onCancelDeleteImage = this.onCancelDeleteImage.bind(this);
    this.onQuillKeyDown = this.onQuillKeyDown.bind(this);
  }

  componentDidMount() { 
    this.attachQuillRefs();
    if (this.quillRef && this.quillRef.root) {
      this.quillRef.root.addEventListener('paste', this.onPaste);
      this.quillRef.root.addEventListener('drop', this.onDrop, false);
      this.quillRef.root.addEventListener('keydown', this.onQuillKeyDown, true);
    }
    this.setState({content: this.props.content});
  }

  componentWillUnmount(): void {
    if (this.quillRef && this.quillRef.root) {
      this.quillRef.root.removeEventListener('paste', this.onPaste);
      this.quillRef.root.removeEventListener('drop', this.onDrop);
      this.quillRef.root.removeEventListener('keydown', this.onQuillKeyDown, true);
    }
  }

  setQuill() {
    let container = [
      this.props.hasFontSize && {'header': [1, 2, 3, false]},
      'bold', 'italic', 'underline', 'strike',
      {'align': ''}, {'align': 'center'}, {'align': 'right'},
      {'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}, 'link', 'image', 'code-block'
    ];

    this.quillModules = {
      toolbar: {
        handlers: {
          image: this.onQuillImage,
          'code-block': this.onEmbedOpen
        },
        container: [container]
      }
    };
    
    if (this.props.isActivity)
      this.quillModules = {toolbar: null};

    this.quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'align', 'list', 'link', 'image', 'audio', 'video', 'embed', 'unfurl', 'loading'];
    if (this.props.isActivity)
      this.quillFormats = ['image', 'video', 'embed', 'audio', 'unfurl', 'loading'];
  }

  attachQuillRefs() {
    // Ensure React-Quill reference is available:
    if (typeof this.reactQuillRef.getEditor !== 'function') return;

    // Skip if Quill reference is defined:
    if (this.quillRef != null) return;
    
    const quillRef = this.reactQuillRef.getEditor();
    if (quillRef != null) {
      this.quillRef = quillRef;
      this.props.getRef(quillRef);
      
      if (this.quillRef.root) {
        this.quillRef.root.addEventListener('keydown', this.onQuillKeyDown, true);
      }
    } 

    if(this.quillRef && this.props.height) {
      (this.quillRef.root as HTMLElement).style.minHeight = this.props.height;
    }

    //clipboard manager for custom blots
    if(this.quillRef) {
      this.quillRef.clipboard.addMatcher('div.ql-embed', (node: HTMLElement, delta: any) => {
        const embed = EmbedBlot.value(node);
        return new Delta().insert({ embed });
      });
      this.quillRef.clipboard.addMatcher('div.ql-unfurl', (node: HTMLElement, delta: any) => {
        const unfurl = UnfurlBlot.value(node);
        return new Delta().insert({ unfurl });
      });
    }
  }

  async onPaste(e:any) {
    if (e.clipboardData && e.clipboardData.items && e.clipboardData.items.length) {
      let item = e.clipboardData.items[0];
      if (IMAGE_MIME_REGEX.test(item.type)) {
        this.uploadFile(item.getAsFile());
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }

    // Check for Ctrl+Shift+V (or Cmd+Shift+V on Mac) to paste plain text without transformations
    if (this.isPlainPaste) {
      this.isPlainPaste = false;
      return; // Allow default paste behavior
    }

    // Check if pasted text is a URL
    if (e.clipboardData) {
      let pastedText = e.clipboardData.getData('text/plain');
      if (pastedText) {
        pastedText = pastedText.trim();
        // Find all URLs in the pasted text
        let urlRegex = /https?:\/\/[^\s]+/gi;
        let urls = pastedText.match(urlRegex);
        // Only unfurl if there's exactly one URL and it's the only content
        if (urls && urls.length === 1 && pastedText === urls[0]) {
          // getSelection() returns null during paste event, so use stored quillPosition
          // Manually update quillPosition by calling onContentChangeSelection
          this.onContentChangeSelection();
          let cursorIndex = this.quillPosition;
          
          // If we have a valid cursor position, check if we're at line start
          if (cursorIndex !== undefined && cursorIndex !== null && cursorIndex >= 0) {
            // Check if cursor is at the beginning of a new line
            let isAtLineStart = false;
            
            if (cursorIndex === 0) {
              // At the very start of the document - always unfurl
              isAtLineStart = true;
            } else {
              // Get text before cursor
              let textBefore = this.quillRef.getText(0, cursorIndex);
              
              // Find the start of the current line (last newline or start of document)
              let lastNewlinePos = textBefore.lastIndexOf('\n');
              let lineStartIndex = lastNewlinePos === -1 ? 0 : lastNewlinePos + 1;
              
              // Get text from start of current line to cursor position
              let textOnCurrentLine = textBefore.substring(lineStartIndex);
              
              // If current line is empty (no text before cursor on this line), we're at line start
              if (textOnCurrentLine.trim().length === 0) {
                isAtLineStart = true;
              }
            }
            
            if (isAtLineStart) {
              e.stopPropagation();
              e.preventDefault();
              this.quillPosition = cursorIndex;
              await this.embedUrl(pastedText);
              setTimeout(() => {
                const length = this.quillRef.getLength();
                this.quillRef.setSelection(length, 0);
              }, 10);
              return;
            }
          }
        }
      }
    }
  }

  onDrop(e:any) {
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
      let file = e.dataTransfer.files[0];
      if (IMAGE_MIME_REGEX.test(file.type)) {
        this.uploadFile(file);
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }
  }

  async uploadFile(file:any) {
    if (file.type.indexOf('image') == -1) {
      this.setState({alert: 'Currently just image files are allowed.'});
      return;
    }
    
    this.setState({message: 'Uploading file...'})

    let response:any = await Server.media.uploadFile(file);

    if(response.success) {
      response = await Server.media.getFile(response.id);

      if(response.success) {
        let url = Server.media.getFileUrl(response.file);
        this.setState({message: ''});
        this.quillRef.insertEmbed(this.quillPosition, 'image', url);
        this.quillRef.setSelection(this.quillPosition+1, 0);
      }
      else
        this.setState({message: '', alert: response.message});
    }
    else
      this.setState({message: '', alert: response.message});
  }

  onContentChange(value: any) {
    this.setState({content: value});

    if (this.props.onChange) {
      let text = this.quillRef.getText().trim();
      text     = text.replace(/[\r\n]/g, ''); // remove \n (enter)
      this.props.onChange(text.length);
    }
  };

  onContentChangeSelection() {
    let range = this.quillRef.getSelection();
    if (range) {
      this.quillPosition = range.index;
      if (this.props.getPosition)
        this.props.getPosition(range.index);
    }
  };

  getElementTypeFromOp(op: any): string | null {
    if (op.insert && typeof op.insert === 'object') {
      if (op.insert.image) return 'image';
      if (op.insert.video) return 'video';
      if (op.insert.audio) return 'audio';
      if (op.insert.unfurl) return 'unfurl';
    }
    return null;
  }

  getElementTypeFromBlot(blot: any): string | null {
    if (blot && blot.statics) {
      const blotName = blot.statics.blotName;
      if (blotName === 'image' || blotName === 'video' || blotName === 'audio' || blotName === 'unfurl') {
        return blotName;
      }
    }
    return null;
  }

  isElementOp(op: any): boolean {
    return this.getElementTypeFromOp(op) !== null;
  }

  isElementBlot(blot: any): boolean {
    return this.getElementTypeFromBlot(blot) !== null;
  }

  onQuillKeyDown(e:any) {
    // Check for Ctrl+Shift+V (or Cmd+Shift+V on Mac) to paste plain text
    if ((e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey) && e.shiftKey) {
      this.isPlainPaste = true;
      // Clear flag after a short delay in case paste doesn't happen
      setTimeout(() => {
        this.isPlainPaste = false;
      }, 100);
    }

    if ((e.key === 'Backspace' || e.key === 'Delete') && this.quillRef) {
      let range = this.quillRef.getSelection();
      if (range) {
        let deleteIndex: number | null = null;
        let elementType: string | null = null;
        let checkIndex = range.index;
        let isDeleteKey = e.key === 'Delete';
        
        if (range.length === 0) {
          if (isDeleteKey) {
            let contents = this.quillRef.getContents(checkIndex, 1);
            if (contents.ops && contents.ops.length > 0) {
              let op = contents.ops[0];
              elementType = this.getElementTypeFromOp(op);
              if (elementType) {
                deleteIndex = checkIndex;
              }
            }
            
            if (deleteIndex === null) {
              try {
                let nextBlot = this.quillRef.getLeaf(checkIndex)[0];
                elementType = this.getElementTypeFromBlot(nextBlot);
                if (elementType) {
                  deleteIndex = checkIndex;
                }
              } catch (err) {
              }
            }
            
            if (deleteIndex === null) {
              let allContents = this.quillRef.getContents();
              let currentPos = 0;
              let nextElementIndex = null;
              
              for (let i = 0; i < allContents.ops.length; i++) {
                let op = allContents.ops[i];
                if (op.insert) {
                  if (this.isElementOp(op)) {
                    if (currentPos >= checkIndex) {
                      nextElementIndex = currentPos;
                      elementType = this.getElementTypeFromOp(op);
                      break;
                    }
                  }
                  if (typeof op.insert === 'string') {
                    currentPos += op.insert.length;
                  } else {
                    currentPos += 1;
                  }
                }
              }
              
              if (nextElementIndex !== null && nextElementIndex === checkIndex) {
                deleteIndex = nextElementIndex;
              }
            }
          } else if (checkIndex > 0) {
            let contents = this.quillRef.getContents(checkIndex - 1, 1);
            if (contents.ops && contents.ops.length > 0) {
              let op = contents.ops[0];
              elementType = this.getElementTypeFromOp(op);
              if (elementType) {
                deleteIndex = checkIndex - 1;
              }
            }
            
            if (deleteIndex === null) {
              try {
                let prevBlot = this.quillRef.getLeaf(checkIndex - 1)[0];
                elementType = this.getElementTypeFromBlot(prevBlot);
                if (elementType) {
                  deleteIndex = checkIndex - 1;
                }
              } catch (err) {
              }
            }
            
            if (deleteIndex === null) {
              let allContents = this.quillRef.getContents();
              let currentPos = 0;
              let lastElementIndex = null;
              
              for (let i = 0; i < allContents.ops.length; i++) {
                let op = allContents.ops[i];
                if (op.insert) {
                  if (this.isElementOp(op)) {
                    if (currentPos < checkIndex) {
                      lastElementIndex = currentPos;
                      elementType = this.getElementTypeFromOp(op);
                    }
                  }
                  if (typeof op.insert === 'string') {
                    currentPos += op.insert.length;
                  } else {
                    currentPos += 1;
                  }
                }
              }
              
              if (lastElementIndex !== null && lastElementIndex < checkIndex) {
                let textAfterElement = this.quillRef.getText(lastElementIndex + 1, checkIndex - lastElementIndex - 1);
                if (textAfterElement === '' || textAfterElement === '\n' || textAfterElement.trim() === '') {
                  deleteIndex = lastElementIndex;
                }
              }
            }
          }
        }
        
        if (deleteIndex !== null && elementType) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          let deleteMessage = '';
          if (elementType === 'unfurl') {
            deleteMessage = 'Are you sure you want to delete this element?';
          } else {
            const elementTypeLabel = elementType === 'image' ? 'image' : elementType === 'video' ? 'video' : 'audio';
            deleteMessage = `Are you sure you want to delete this ${elementTypeLabel}?`;
          }
          this.setState({
            deleteImageQuestion: deleteMessage,
            pendingDeleteIndex: deleteIndex,
            pendingDeleteSelection: { index: range.index, length: range.length }
          });
          return false;
        }
      }
    }
  }

  onContentKeyDown(e:any) {
    if (this.props.onKeyDown) 
      this.props.onKeyDown(e);
  }

  onConfirmDeleteImage() {
    if (this.quillRef && this.state.pendingDeleteIndex !== null) {
      this.quillRef.deleteText(this.state.pendingDeleteIndex, 1);
      this.setState({
        deleteImageQuestion: '',
        pendingDeleteIndex: null,
        pendingDeleteSelection: null
      });
    }
  }

  onCancelDeleteImage() {
    let savedSelection = this.state.pendingDeleteSelection;
    let savedIndex = this.state.pendingDeleteIndex;
    
    this.setState({
      deleteImageQuestion: '',
      pendingDeleteIndex: null,
      pendingDeleteSelection: null
    }, () => {
      if (this.quillRef) {
        if (savedSelection) {
          this.quillRef.setSelection(savedSelection.index, savedSelection.length);
        } else if (savedIndex !== null) {
          this.quillRef.setSelection(savedIndex + 1, 0);
        }
        this.quillRef.focus();
      }
    });
  }

  onQuillImage() {
    this.setState({openAddMedia: true});
  }

  onInsertMedia(data: any) {
    this.setState({openAddMedia: false});
    this.quillRef.insertEmbed(this.quillPosition, data.category, data.url);
    this.quillRef.setSelection(this.quillPosition+1, 0);
  }

  onEmbedOpen() {
    this.setState({openAddEmbed: true});
  }

  async onEmbed(url:string) {
    this.setState({openAddEmbed: false});
    await this.embedUrl(url);
  }

  onEmbedClose() {
    this.setState({openAddEmbed: false}); 
  }

  replaceLoadingBlot(loadingIndex: number, embedType: string, embedData: any) {
    this.quillRef.deleteText(loadingIndex, 1);
    this.quillRef.insertEmbed(loadingIndex, embedType, embedData);
  }

  replaceLoadingBlotWithText(loadingIndex: number, url: string) {
    this.quillRef.deleteText(loadingIndex, 1);
    this.quillRef.insertText(loadingIndex, url + '\n');
    this.quillRef.formatText(loadingIndex, url.length, 'link', url);
  }

  removeLoadingBlot(loadingIndex: number) {
    this.quillRef.deleteText(loadingIndex, 1);
  }

  async embedUrl(url:string, addTextLinkOnFailure: boolean = true): Promise<boolean> {
    this.setState({isUnfurling: true});
    
    let success = false;
    
    try {
      let loadingIndex = this.quillPosition;
      
      let textBefore = this.quillRef.getText(0, loadingIndex);
      if(textBefore.length > 0 && !textBefore.endsWith('\n')) {
        this.quillRef.insertText(loadingIndex, '\n');
        loadingIndex = loadingIndex + 1;
      }
      
      this.quillRef.insertEmbed(loadingIndex, 'loading', {});
      let imageRegex = /(https?:\/\/.*\.(?:png|jpg|jpeg|gif|webp))/i;
      let audioRegex = /(https?:\/\/.*\.(?:wav|mp3|ogg|aac|m4a|flac))/i;
      let isImage = imageRegex.test(url);
      let isVideo = isVideoUrl(url);
      let isAudio = audioRegex.test(url);

      let isLocal = (url.indexOf(window.location.host) != -1);
      let isSpotify = url.indexOf('https://open.spotify.com/') == 0;
      let isSoundCloud = url.indexOf('https://soundcloud.com/') == 0;
      let isYouTube = url.indexOf('https://www.youtube.com/') == 0 || url.indexOf('https://music.youtube.com/') == 0 || url.indexOf('https://youtu.be/') == 0;
      let isDropBox = url.indexOf('https://www.dropbox.com/') == 0;
      let isVimeo = url.indexOf('https://vimeo.com/') == 0;
      let isTwitch = url.indexOf('https://twitch.tv/') == 0;

      if(isTwitch)
        isVideo = false;

      if(isLocal) {
        try {
          let parts = url.split('/');
          let page = parts[3];
          let embedded = false;

          if(page == 'blog') {
            let response = await Server.blogs.getBlog({slug: parts[4]});
            if(response.success) {
              let blog = response.blog;
              
              await Server.public.loadProfiles([blog.author]);
              let author = Server.public.getProfile(blog.author);
              let description = getFirstParagraph(blog.content);
            
              this.replaceLoadingBlot(loadingIndex, 'unfurl', {
                title: blog.title,
                author: author.name,
                url: url,
                image: blog.image,
                description
              });

              embedded = true;
            }
          }
          else if(page == 'forum' && parts.length == 6) {
            let response:any = await Server.forums.getForum(parts[4]);
            if(response.success) {
              let forum = response.forum;          
              response = await Server.forums.getTopic(parts[4], parts[5]);
              if(response.success) {
                let topic = response.topic;

                response = await Server.forums.getPosts(parts[4], parts[5], {limit: 1});

                if(response.success) {
                  let post = response.posts[0];
                  let description = getFirstParagraph(post.content);

                  await Server.public.loadProfiles([post.author]);
                  let author = Server.public.getProfile(post.author);
      
                  this.replaceLoadingBlot(loadingIndex, 'unfurl', {
                    title: `${forum.title} > ${topic.title}`,
                    author: author.name,
                    url: url,
                    description
                  });
      
                  embedded = true;
                }
              }
            }
          }

          if(embedded) {
            success = true;
          } else {
            if(addTextLinkOnFailure) {
              this.replaceLoadingBlotWithText(loadingIndex, url);
            } else {
              this.removeLoadingBlot(loadingIndex);
            }
          }
        } catch (error) {
          if(addTextLinkOnFailure) {
            this.replaceLoadingBlotWithText(loadingIndex, url);
          } else {
            this.removeLoadingBlot(loadingIndex);
          }
        }
      }
      else if(isDropBox) {
        url = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
        let type = isAudio ? 'audio' : (isImage ? 'image' : 'video');
        this.replaceLoadingBlot(loadingIndex, type, url);
        success = true;
      }
      else if(isVimeo) {
        try {
          let response = await fetch(`https://vimeo.com/api/oembed.json?url=${url}`);
          let json = await response.json();
          this.replaceLoadingBlot(loadingIndex, 'embed', {html: json.html, video: true});
          success = true;
        } catch (error) {
          if(addTextLinkOnFailure) {
            this.replaceLoadingBlotWithText(loadingIndex, url);
          } else {
            this.removeLoadingBlot(loadingIndex);
          }
        }
      }
      else if(isYouTube) {
        try {
          let response = await fetch(`https://www.youtube.com/oembed?format=json&url=${url}`);
          let json = await response.json();
          this.replaceLoadingBlot(loadingIndex, 'embed', {html: json.html, video: true});
          success = true;
        } catch (error) {
          if(addTextLinkOnFailure) {
            this.replaceLoadingBlotWithText(loadingIndex, url);
          } else {
            this.removeLoadingBlot(loadingIndex);
          }
        }
      }
      else if(isSpotify) {
        try {
          let response = await fetch(`https://open.spotify.com/oembed?url=${url}`);
          let json = await response.json();
          this.replaceLoadingBlot(loadingIndex, 'embed', {html: json.html, spotify: true});
          success = true;
        } catch (error) {
          if(addTextLinkOnFailure) {
            this.replaceLoadingBlotWithText(loadingIndex, url);
          } else {
            this.removeLoadingBlot(loadingIndex);
          }
        }
      }
      else if(isSoundCloud) {
        try {
          let response = await fetch(`https://soundcloud.com/oembed?format=json&url=${url}`);
          let json = await response.json();
          this.replaceLoadingBlot(loadingIndex, 'embed', {html: json.html, video: true});
          success = true;
        } catch (error) {
          if(addTextLinkOnFailure) {
            this.replaceLoadingBlotWithText(loadingIndex, url);
          } else {
            this.removeLoadingBlot(loadingIndex);
          }
        }
      }
      else if(isImage) {
        this.replaceLoadingBlot(loadingIndex, 'image', url);
        success = true;
      }
      else if(isVideo) {
        this.replaceLoadingBlot(loadingIndex, 'video', getPublicVideoUrl(url));
        success = true;
      }
      else {
        let response = await Server.user.unfurl(url);

        if(response.success) {
          let data = response.metadata
          if(!data.url)
            data.url = url
          this.replaceLoadingBlot(loadingIndex, 'unfurl', data);
          success = true;
        }
        else {
          if(addTextLinkOnFailure) {
            this.replaceLoadingBlotWithText(loadingIndex, url);
          } else {
            this.removeLoadingBlot(loadingIndex);
          }
        }
      }
    } finally {
      this.setState({isUnfurling: false});
    }
    
    return success;
  }

  setContent(content: string) {
    this.setState({content});
  }

  render() {
    this.setQuill();

    return (
      <div>
        <ReactQuill
          theme="snow"
          value={this.state.content}
          onChange={this.onContentChange}
          onChangeSelection={this.onContentChangeSelection}
          onKeyDown={this.onContentKeyDown}
          modules={this.quillModules}
          formats={this.quillFormats}
          placeholder={this.props.placeholder ? this.props.placeholder : 'Enter...'}
          readOnly={this.state.isUnfurling}
          ref={(el) => { this.reactQuillRef = el }}
        />

        <AddMediaModal 
          open={this.state.openAddMedia} 
          onClose={()=>this.setState({openAddMedia: false})} 
          callback={(data:any)=>this.onInsertMedia(data)} 
        />

        <AddEmbedModal 
          open={this.state.openAddEmbed} 
          onEmbed={(url:string)=>this.onEmbed(url)}
          onClose={()=>this.onEmbedClose()} 
          quillRef={this.quillRef}
          quillPosition={this.quillPosition}
        />

        <MessageModal message={this.state.message} />
        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert:''})}/>
        <QuestionModal 
          message={this.state.deleteImageQuestion} 
          onYes={this.onConfirmDeleteImage} 
          onNo={this.onCancelDeleteImage} 
        />
      </div>
    )
  }
}

export default SharedQuillEditor;